import {useCallback, useState, type KeyboardEvent} from 'react';
import {IProductOptionSelection} from '../../../../../../types/galleryTypes';

export const useRadioButtonKeyboardNavigation = ({
  containerRef,
  visibleSelections,
  isSelectionDisabled,
  handleSelection,
}: {
  containerRef: React.RefObject<HTMLDivElement>;
  visibleSelections: IProductOptionSelection[];
  isSelectionDisabled: (selectionId: number) => boolean;
  handleSelection: (pickedSelectionId: number) => void;
}) => {
  const [focusedIndex, setFocusedIndex] = useState(0);

  const navigate = useCallback(
    (direction: number) => {
      const length = visibleSelections.length;
      let nextIndex = (focusedIndex + direction + length) % length;

      while (isSelectionDisabled(visibleSelections[nextIndex].id)) {
        nextIndex = (nextIndex + direction + length) % length;
      }

      const radioButtons = containerRef.current?.querySelectorAll<HTMLInputElement>('input[type="radio"]');
      radioButtons[nextIndex]?.focus();
      setFocusedIndex(nextIndex);
    },
    [focusedIndex, visibleSelections, isSelectionDisabled, containerRef]
  );

  const handleKeyboardSelection = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          event.stopPropagation();
          navigate(1);
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          event.stopPropagation();
          navigate(-1);
          break;
        case 'Enter':
        case ' ':
          handleSelection(visibleSelections[focusedIndex].id);
          break;
      }
    },
    [navigate, handleSelection, visibleSelections, focusedIndex]
  );

  return {containerRef, handleKeyboardSelection};
};
